import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  Divider,
  IconButton,
  alpha,
  Stack,
} from "@mui/material";
import { Close, Save, LocalHospital } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import useClientDetails from "../../../../../hooks/useClientDetails";
import { QK } from "../../../../../base/qk";
import { getVisitByClientID } from "../../../../../api/services/scheduling";
import {
  getMedication,
  getMedicineNames,
  updateMedication,
} from "../../../../../api/services/medication";
import { medicationValidationSchema } from "../Medication.yup.config";
import MedicationBasicInfo from "./Medications/MedicationBasicInfo";
import MedicationScheduleInfo from "./Medications/MedicationScheduleInfo";
import MedicationInventorySection from "./Medications/MedicationInventorySection";
import MedicationTimingSection from "./Medications/MedicationTimingSection";
import { routeAdminOptions } from "../medication.config";
import Loader from "../../../../Loader/Loader";

const initialFormData = {
  title: "",
  medicine_type: "Individual Medication",
  controlled_medication: false,
  medication_type: "",
  route_administration: "",
  frequency: "",
  start_date: "",
  end_date: "",
  expiry_date: "",
  how_assisted: "",
  every: "",
  notes: "",
  reason_for_medication: "",
  number_of_cards: "",
  slots_per_card: "",
  loose_slots: "",
  total_slots_remaining: "",
};

const UpdateMedicationModal = ({ open, handleClose, MedicineId }) => {
  const { id: clientID } = useParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { fullName } = useClientDetails(clientID);

  const [formData, setFormData] = useState(initialFormData);
  const [individualMeds, setIndividualMeds] = useState([
    { name: "", dose: "", initial_stock_qty: "" },
  ]);
  const [times, setTimes] = useState([{ time: "", visit: null }]);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setIsSubmitting(false);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleIndMedChange = (index, field, value) => {
    const updated = [...individualMeds];
    updated[index][field] = value;
    setIndividualMeds(updated);
  };

  const handleTimeChange = (index, field, value) => {
    let actualValue = value;
    if (value && typeof value === "object" && value.target) {
      actualValue = value.target.value;
    }
    const updated = [...times];
    updated[index][field] = actualValue;
    setTimes(updated);
  };

  const handleDaySelection = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const { data: medication, isLoading: medicationLoading } = useQuery(
    [MedicineId],
    () => getMedication(MedicineId),
    { enabled: !!MedicineId && open }
  );

  const { data: visits } = useQuery([QK.visits, clientID], () =>
    getVisitByClientID(clientID)
  );

  const { data: medicineNames, isLoading: isSearchingMedicines } = useQuery(
    [QK.medicineNames, search],
    () => getMedicineNames(search),
    { enabled: search.length >= 2 }
  );

  useEffect(() => {
    if (medication && open && visits) {
      setSelectedDays(medication.weekdays || []);

      const dbRoute = medication.route_administration;
      const matchedRouteObject = routeAdminOptions.find(
        (opt) => opt.value === dbRoute || opt.label === dbRoute
      );

      const visitMap = {};
      visits.forEach((v) => {
        visitMap[v.id] = v.visit_name;
      });

      const mappedTimes =
        medication.time_data?.length > 0
          ? medication.time_data.map((t) => ({
              id: t.id,
              time: t.time,
              visit: t.visit
                ? {
                    value: t.visit,
                    label: visitMap[t.visit] || `Visit ${t.visit}`,
                  }
                : null,
            }))
          : [{ time: "", visit: null }];

      const mappedIndMeds =
        medication.individual_meds?.length > 0
          ? medication.individual_meds.map((m) => ({
              name: m.name || "",
              dose: m.dose || "",
              initial_stock_qty:
                m.remaining_stock !== undefined
                  ? m.remaining_stock
                  : m.initial_stock_qty,
            }))
          : [{ name: "", dose: "", initial_stock_qty: "" }];

      setFormData({
        title: medication.title || "",
        medicine_type: medication.medicine_type || "Individual Medication",
        controlled_medication: medication.controlled_medication || false,
        medication_type: medication.medication_type || "",
        route_administration: matchedRouteObject?.value || dbRoute || "",
        frequency: medication.frequency || "",
        start_date: medication.start_date || "",
        end_date: medication.end_date || null,
        expiry_date: medication.expiry_date || null,
        how_assisted: medication.how_assisted || "",
        every: medication.every || "",
        notes: medication.notes || "",
        reason_for_medication: medication.reason_for_medication || "",
        number_of_cards:
          medication.number_of_cards !== null &&
          medication.number_of_cards !== undefined
            ? medication.number_of_cards
            : "",
        slots_per_card:
          medication.slots_per_card !== null &&
          medication.slots_per_card !== undefined
            ? medication.slots_per_card
            : "",
        loose_slots:
          medication.loose_slots !== null &&
          medication.loose_slots !== undefined
            ? medication.loose_slots
            : "",
        total_slots_remaining:
          medication.total_slots_remaining !== null &&
          medication.total_slots_remaining !== undefined
            ? medication.total_slots_remaining
            : "",
      });

      setIndividualMeds(mappedIndMeds);
      setTimes(mappedTimes);

      if (medication.medicine_lists?.length > 0) {
        const formatted = medication.medicine_lists.map((m) => ({
          value: m.id,
          label: m.name,
        }));
        setSelectedMedications(formatted);
      } else {
        setSelectedMedications([]);
      }
    }
  }, [medication, open, visits]);

  const validate = async () => {
    try {
      const normalizedTimes = times.map((t) => {
        let timeVal = t.time;
        if (timeVal && typeof timeVal === "object" && timeVal.target) {
          timeVal = timeVal.target.value;
        }
        return {
          time: timeVal || "",
          visit: t.visit?.value || t.visit?.id || t.visit || null,
        };
      });

      const payload = {
        ...formData,
        individual_meds: individualMeds,
        med_times: normalizedTimes,
        weekdays: selectedDays,
        medicine_list: selectedMedications.map((m) => m.value),
      };
      await medicationValidationSchema.validate(payload, { abortEarly: false });
      setErrors({});
      return { valid: true, errors: {} };
    } catch (err) {
      const newErrors = {};
      if (err && err.inner && Array.isArray(err.inner)) {
        err.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });
      } else if (err && err.message) {
        newErrors._global = err.message;
      }
      setErrors(newErrors);
      return { valid: false, errors: newErrors };
    }
  };

  const onSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const result = await validate();
    if (!result.valid) {
      setIsSubmitting(false);
      return;
    }

    const normalizedTimes = times.map((t) => {
      let timeVal = t.time;
      if (timeVal && typeof timeVal === "object" && timeVal.target) {
        timeVal = timeVal.target.value;
      }
      return {
        id: t.id,
        time: timeVal || "",
        visit: t.visit?.value || t.visit?.id || t.visit || null,
      };
    });

    const finalData = {
      ...formData,
      client: clientID,
      weekdays: selectedDays,
      med_times: normalizedTimes,
      individual_meds:
        formData.medicine_type === "Individual Medication"
          ? individualMeds.map((m) => ({
              ...m,
              initial_stock_qty: Number(m.initial_stock_qty) || 0,
            }))
          : [],
      medicine_list:
        formData.medicine_type === "Blister Pack"
          ? selectedMedications.map((m) => m.value)
          : [],
      name:
        formData.medicine_type === "Individual Medication"
          ? individualMeds[0]?.name
          : formData.title,
      number_of_cards:
        formData.medicine_type === "Blister Pack"
          ? formData.number_of_cards === ""
            ? null
            : Number(formData.number_of_cards)
          : null,
      slots_per_card:
        formData.medicine_type === "Blister Pack"
          ? formData.slots_per_card === ""
            ? null
            : Number(formData.slots_per_card)
          : null,
      loose_slots:
        formData.medicine_type === "Blister Pack"
          ? formData.loose_slots === ""
            ? null
            : Number(formData.loose_slots)
          : null,
      total_slots_remaining:
        formData.medicine_type === "Blister Pack"
          ? formData.total_slots_remaining === ""
            ? null
            : Number(formData.total_slots_remaining)
          : null,
    };

    try {
      const response = await updateMedication(MedicineId, finalData);
      if (response && (response.status === 200 || response.status === 204)) {
        enqueueSnackbar("Medication Updated Successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.clientMedications, clientID]);
        queryClient.invalidateQueries([QK.medicationDetails, MedicineId]);
        handleModalClose();
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Update failed",
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setFormData(initialFormData);
    setIndividualMeds([{ name: "", dose: "", initial_stock_qty: "" }]);
    setTimes([{ time: "", visit: null }]);
    setSelectedMedications([]);
    setSelectedDays([]);
    setErrors({});
    setSearch("");
    setIsSubmitting(false);
    handleClose();
  };

  const visitOptions =
    visits?.map((v) => ({ value: v.id, label: v.visit_name })) || [];

  if (medicationLoading) return <Loader />;

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" } }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: 920, md: 1020 },
          bgcolor: "#ffffff",
          boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
          borderRadius: 3,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 2, sm: 3, md: 4 },
            py: 2.5,
            borderBottom: "2px solid #f1f5f9",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: alpha("#2469F0", 0.08),
                color: "#2469F0",
                p: 1,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
              }}
            >
              <LocalHospital sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.05rem" }}
              >
                Update Medication Setup
              </Typography>
              <Typography
                sx={{ fontSize: "0.78rem", color: "#64748b", mt: 0.25 }}
              >
                For:{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: "#0f172a" }}
                >
                  {fullName}
                </Box>
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleModalClose}
            sx={{ bgcolor: "#f1f5f9", "&:hover": { bgcolor: "#e2e8f0" } }}
          >
            <Close />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            py: 3,
          }}
        >
          <MedicationBasicInfo
            formData={formData}
            handleChange={handleChange}
            errors={errors}
          />

          <Divider sx={{ my: 3 }} />

          <MedicationScheduleInfo
            formData={formData}
            handleChange={handleChange}
            errors={errors}
          />

          <MedicationInventorySection
            formData={formData}
            handleChange={handleChange}
            individualMeds={individualMeds}
            handleIndMedChange={handleIndMedChange}
            setIndividualMeds={setIndividualMeds}
            selectedMedications={selectedMedications}
            setSelectedMedications={setSelectedMedications}
            medicineNames={medicineNames}
            isSearchingMedicines={isSearchingMedicines}
            errors={errors}
            search={search}
            setSearch={setSearch}
          />

          <MedicationTimingSection
            formData={formData}
            handleChange={handleChange}
            times={times}
            handleTimeChange={handleTimeChange}
            setTimes={setTimes}
            selectedDays={selectedDays}
            handleDaySelection={handleDaySelection}
            visitOptions={visitOptions}
            errors={errors}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            px: { xs: 2, sm: 3, md: 4 },
            py: 2.5,
            borderTop: "2px solid #f1f5f9",
            bgcolor: "#ffffff",
            flexShrink: 0,
          }}
        >
          <Button
            type="button"
            onClick={handleModalClose}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.1,
              fontWeight: 600,
              textTransform: "none",
              borderColor: "#e2e8f0",
              color: "#64748b",
              "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            variant="contained"
            startIcon={<Save sx={{ fontSize: 18 }} />}
            sx={{
              px: 4,
              py: 1.1,
              borderRadius: 2,
              bgcolor: "#2469F0",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.85rem",
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0 4px 16px rgba(36, 105, 240, 0.3)",
              },
              "&.Mui-disabled": {
                bgcolor: "#93C5FD",
                color: "#ffffff",
              },
            }}
          >
            {isSubmitting ? "Updating..." : "Update Medication"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpdateMedicationModal;